import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import TutorialStep from './TutorialStep';
import { playSound } from '../../utils/soundManager';

const Tutorial = ({ onComplete, onSkip, profileId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const tutorialSteps = [
    {
      id: 'welcome',
      title: 'Welcome to JR Learning! 🎓',
      description: 'Let\'s take a quick tour to help you get started!',
      icon: '👋',
      highlight: null,
      position: 'center'
    },
    {
      id: 'categories',
      title: 'Choose Your Category 📚',
      description: 'Select from 15 exciting categories like Math, Science, Geography, and more!',
      icon: '🎯',
      highlight: '.category-grid',
      position: 'bottom'
    },
    {
      id: 'difficulty',
      title: 'Set Your Difficulty 🎯',
      description: 'Choose Easy, Medium, or Hard based on your age and skill level. The app will suggest the best level for you!',
      icon: '🌟',
      highlight: '.difficulty-badge',
      position: 'bottom'
    },
    {
      id: 'quiz-mode',
      title: 'Pick Your Mode ⏱️',
      description: 'Practice Mode for relaxed learning or Timed Mode for an exciting challenge with bonus points!',
      icon: '🎮',
      highlight: '.quiz-mode-badge',
      position: 'bottom'
    },
    {
      id: 'badges',
      title: 'Earn Badges 🏆',
      description: 'Complete quizzes and achieve milestones to unlock 60+ awesome badges!',
      icon: '⭐',
      highlight: '.badge-count',
      position: 'bottom'
    },
    {
      id: 'streak',
      title: 'Build Your Streak 🔥',
      description: 'Play every day to build your learning streak and earn special streak badges!',
      icon: '📅',
      highlight: null,
      position: 'center'
    },
    {
      id: 'dashboard',
      title: 'Track Your Progress 📊',
      description: 'Visit the Dashboard to see your stats, download reports, and monitor your learning journey!',
      icon: '📈',
      highlight: 'nav button:nth-child(2)',
      position: 'bottom'
    },
    {
      id: 'ready',
      title: 'You\'re All Set! 🚀',
      description: 'Ready to start learning? Choose a category and begin your adventure!',
      icon: '🎉',
      highlight: null,
      position: 'center'
    }
  ];

  const currentStepData = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;

  useEffect(() => {
    // Highlight the target element
    if (currentStepData.highlight) {
      const element = document.querySelector(currentStepData.highlight);
      if (element) {
        element.classList.add('tutorial-highlight');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return () => {
      // Remove highlight from all elements
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
    };
  }, [currentStep, currentStepData.highlight]);

  const handleNext = () => {
    playSound('click');
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    playSound('click');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    playSound('click');
    setIsVisible(false);
    setTimeout(() => {
      saveTutorialCompleted(profileId);
      onSkip();
    }, 300);
  };

  const handleComplete = () => {
    playSound('success');
    setIsVisible(false);
    setTimeout(() => {
      saveTutorialCompleted(profileId);
      onComplete();
    }, 300);
  };

  const saveTutorialCompleted = (profileId) => {
    try {
      localStorage.setItem(`tutorial_completed_${profileId}`, 'true');
    } catch (error) {
      console.error('Error saving tutorial status:', error);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="tutorial-overlay"></div>

      {/* Tutorial Step */}
      <TutorialStep
        step={currentStepData}
        currentStep={currentStep + 1}
        totalSteps={tutorialSteps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        isLastStep={isLastStep}
        isFirstStep={currentStep === 0}
      />

      {/* Progress Dots */}
      <div className="tutorial-progress">
        {tutorialSteps.map((_, index) => (
          <div
            key={index}
            className={`progress-dot ${index === currentStep ? 'active' : ''} ${
              index < currentStep ? 'completed' : ''
            }`}
            onClick={() => {
              playSound('click');
              setCurrentStep(index);
            }}
          />
        ))}
      </div>
    </>
  );
};

// Check if tutorial should be shown
export const shouldShowTutorial = (profileId) => {
  try {
    const completed = localStorage.getItem(`tutorial_completed_${profileId}`);
    return completed !== 'true';
  } catch (error) {
    return true;
  }
};

// Reset tutorial for testing
export const resetTutorial = (profileId) => {
  try {
    localStorage.removeItem(`tutorial_completed_${profileId}`);
  } catch (error) {
    console.error('Error resetting tutorial:', error);
  }
};

export default Tutorial;
